import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function TermsOfUse() {
  return (
    <div className="min-h-screen bg-[#0A1F3B]" data-testid="terms-page">
      <nav className="sticky top-0 z-50 bg-[#0A1F3B]/80 backdrop-blur-xl border-b border-[#1C3D66]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-1">
            <span className="font-heading text-2xl font-light text-[#F5D300]">oly</span>
            <span className="font-heading text-2xl font-black text-white">Payroll</span>
          </Link>
          <Link to="/" className="text-sm text-[#94A3B8] hover:text-white flex items-center gap-1"><ArrowLeft size={14} /> Back</Link>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-6 sm:px-8 py-16">
        <h1 className="font-heading text-3xl sm:text-4xl font-black text-white mb-2">End User License Agreement &amp; Terms of Use</h1>
        <p className="text-sm text-[#94A3B8] mb-10">Last updated: May 1, 2026</p>

        <div className="prose-legal space-y-8 text-sm text-[#94A3B8] leading-relaxed">
          <section>
            <h2 className="text-lg font-bold text-white mb-2">1. Acceptance of Terms</h2>
            <p>By accessing or using oly Payroll (&ldquo;the Service&rdquo;), operated by oly (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;), you agree to be bound by these Terms of Use. If you do not agree, do not use the Service.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">2. Description of Service</h2>
            <p>oly Payroll is a web-based tool that processes employee shift data exported from Clover POS systems and generates payroll-ready reports. The Service includes CSV upload and processing, overtime and break violation calculations, tip distribution, Excel report generation, and optional Clover POS Bridge device integration.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">3. Account Registration</h2>
            <p>You may use the Service for one free payroll run without an account. Continued use requires account registration. You are responsible for maintaining the confidentiality of your login credentials and for all activity under your account. You agree to provide accurate, current information and to notify us immediately of any unauthorized use.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">4. Subscription Plans and Payment</h2>
            <p className="mb-2"><span className="text-white font-semibold">Free Tier:</span> One payroll export at no cost, no signup required.</p>
            <p className="mb-2"><span className="text-white font-semibold">Pay As You Go:</span> $5 per individual payroll export.</p>
            <p className="mb-2"><span className="text-white font-semibold">Unlimited Monthly:</span> $19/month for unlimited payroll runs and premium features.</p>
            <p className="mb-2"><span className="text-white font-semibold">White-Glove:</span> $99/month managed payroll preparation service.</p>
            <p>All payments are processed securely by Stripe. Subscriptions renew automatically unless cancelled. You may cancel at any time; access continues through the end of your billing period. Refunds are handled on a case-by-case basis at our discretion.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">5. Permitted Use</h2>
            <p>You may use the Service for lawful payroll processing purposes for your business. You may not:</p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Use the Service for any unlawful purpose</li>
              <li>Attempt to reverse-engineer, decompile, or disassemble the Service</li>
              <li>Upload malicious files, viruses, or harmful code</li>
              <li>Resell, sublicense, or redistribute the Service without written consent</li>
              <li>Interfere with or disrupt the Service or its infrastructure</li>
              <li>Exceed reasonable usage limits (e.g., automated bulk uploads beyond normal business use)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">6. Data Ownership and Responsibility</h2>
            <p>You retain ownership of all payroll data you upload to the Service. You are solely responsible for the accuracy, legality, and completeness of data you submit. We do not verify the correctness of shift data, wage rates, or tax calculations. The Service provides tools to assist with payroll preparation — it does not constitute tax, legal, or accounting advice.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">7. Clover POS Bridge</h2>
            <p>If you use the oly Payroll Prep app on Clover devices, additional terms apply: (a) The app accesses shift and employee data from your Clover account with your authorization. (b) Data synced from Clover is processed on our servers for payroll report generation. (c) We may disable app access (kill switch) for maintenance, security, or compliance reasons. (d) The Clover app is provided &ldquo;as is&rdquo; and is subject to Clover&apos;s developer terms.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">8. Disclaimer of Warranties</h2>
            <p>THE SERVICE IS PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE&rdquo; WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR COMPLETELY SECURE.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">9. Limitation of Liability</h2>
            <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, OLY AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR BUSINESS OPPORTUNITIES, ARISING FROM YOUR USE OF THE SERVICE. OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNTS PAID BY YOU TO US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">10. Indemnification</h2>
            <p>You agree to indemnify and hold harmless oly and its affiliates from any claims, damages, losses, or expenses (including legal fees) arising from your use of the Service, your violation of these Terms, or your violation of any third-party rights.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">11. Modifications to Service and Terms</h2>
            <p>We reserve the right to modify, suspend, or discontinue the Service at any time. We may update these Terms from time to time. Continued use of the Service after changes constitutes acceptance of the revised Terms. Material changes will be communicated via email or prominent notice on the website.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">12. Termination</h2>
            <p>We may terminate or suspend your account at our sole discretion for violation of these Terms or for any other reason. Upon termination, your right to use the Service ceases immediately. You may request export of your data prior to termination.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">13. Governing Law</h2>
            <p>These Terms shall be governed by and construed in accordance with the laws of the State of California, without regard to conflict of law provisions. Any disputes arising from these Terms or the Service shall be resolved in the courts of California.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">14. Contact</h2>
            <p>For questions about these Terms, contact us at <a href="mailto:support@olypayroll.com" className="text-[#F5D300] hover:underline">support@olypayroll.com</a>.</p>
          </section>
        </div>
      </main>

      <footer className="border-t border-[#1C3D66] py-6 px-6 sm:px-8">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-1">
            <span className="font-heading text-lg font-light text-[#F5D300]">oly</span>
            <span className="font-heading text-lg font-black text-white">Payroll</span>
          </div>
          <div className="flex items-center gap-4 text-xs text-[#475569]">
            <Link to="/privacy" className="hover:text-[#94A3B8]">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-[#94A3B8]">Terms of Use</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
